import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * useBadgeCounts — resolves lightweight badge numbers for the sidebar /
 * mobile bottom-nav, so busy shops have a quick visual cue for what still
 * needs their attention today.
 *
 * Currently returns:
 *   • diaryTodayRemaining — non-cancelled, non-completed bookings whose
 *     `starts_at` falls in the *rest of today* (from `now` → end of day
 *     local time).  Scopes to the caller's shop (barbers see just their
 *     own chair — matches the barber-view Diary they land on).
 *
 * Cheap enough to refresh every 60s. Missing session / no shop_id
 * silently returns zeros so we can drop the hook into `AppShell` without
 * caring about the impersonation timing.
 */
export function useBadgeCounts(profile) {
  const shopId = profile?.shop_id;
  const role = profile?.role;
  const [counts, setCounts] = useState({ diaryTodayRemaining: 0 });

  const fetchCounts = useCallback(async () => {
    if (!shopId) { setCounts({ diaryTodayRemaining: 0 }); return; }
    try {
      const now = new Date();
      const endOfDay = new Date(now);
      endOfDay.setHours(23, 59, 59, 999);

      let q = supabase
        .from('bookings')
        .select('id', { count: 'exact', head: true })
        .eq('shop_id', shopId)
        .in('status', ['confirmed', 'pending'])
        .gte('starts_at', now.toISOString())
        .lte('starts_at', endOfDay.toISOString());

      // Barbers only see their own chair — mirror the Diary default
      if (role === 'barber') {
        // Get barber row bound to this user
        const { data: b } = await supabase
          .from('barbers')
          .select('id')
          .eq('user_id', profile?.id)
          .maybeSingle();
        if (b?.id) q = q.eq('barber_id', b.id);
      }

      const { count } = await q;
      setCounts({ diaryTodayRemaining: count || 0 });
    } catch {
      setCounts({ diaryTodayRemaining: 0 });
    }
  }, [shopId, role, profile?.id]);

  useEffect(() => {
    fetchCounts();
    // Refresh every 60s so counts stay live without hammering the DB.
    const t = setInterval(fetchCounts, 60_000);
    // Also refresh when the tab becomes visible again after being hidden.
    const onVis = () => { if (!document.hidden) fetchCounts(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, [fetchCounts]);

  return counts;
}
